/**
 * Categorías de catálogo (una película puede estar en varias).
 * Los géneros se unifican por nombre canónico (Terror ≈ TERROR ≈ terror).
 */

function normalizeTitle(title) {
  return String(title || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** Clave estable para comparar géneros (sin tildes, minúsculas, espacios). */
export function categoryKey(name) {
  return normalizeTitle(String(name || "").trim()).replace(/\s+/g, " ");
}

/**
 * Nombres canónicos del catálogo (cómo se muestran en UI).
 * Clave = categoryKey(nombre).
 */
const CANONICAL_GENRES = {
  accion: "Acción",
  action: "Acción",
  aventura: "Aventura",
  adventure: "Aventura",
  animacion: "Animación",
  animation: "Animación",
  anime: "Anime",
  comedia: "Comedia",
  comedy: "Comedia",
  crimen: "Crimen",
  crime: "Crimen",
  documental: "Documental",
  documentary: "Documental",
  drama: "Drama",
  familia: "Familia",
  family: "Familia",
  fantasia: "Fantasía",
  fantasy: "Fantasía",
  historia: "Historia",
  history: "Historia",
  terror: "Terror",
  horror: "Terror",
  musica: "Música",
  music: "Música",
  misterio: "Misterio",
  mystery: "Misterio",
  romance: "Romance",
  "ciencia ficcion": "Ciencia ficción",
  "science fiction": "Ciencia ficción",
  sci: "Ciencia ficción",
  scifi: "Ciencia ficción",
  "sci fi": "Ciencia ficción",
  suspense: "Suspense",
  thriller: "Suspense",
  belica: "Bélica",
  war: "Bélica",
  western: "Western",
  "pelicula de tv": "Película de TV",
  "tv movie": "Película de TV",
  superheroes: "Superhéroes",
  superheroe: "Superhéroes",
  "sin categoria": "Sin categoría",
  "action adventure": "Acción",
  "sci fi fantasy": "Ciencia ficción",
  "war politics": "Bélica",
  kids: "Familia",
};

/** Categorías que no deben mostrarse como fila de catálogo (películas). */
export const HIDDEN_CATALOG_CATEGORIES = new Set(["cine", "sin categoria"]);

/**
 * Devuelve el nombre canónico de un género.
 * Si no está en el mapa, Title Case suave (primera letra mayúscula).
 */
export function canonicalCategory(name) {
  const raw = String(name || "").trim();
  if (!raw) return "Sin categoría";
  const key = categoryKey(raw);
  if (key === "cine") return "Sin categoría";
  if (CANONICAL_GENRES[key]) return CANONICAL_GENRES[key];
  // Unificar espacios y capitalizar primera letra de cada palabra significativa.
  return raw
    .replace(/\s+/g, " ")
    .split(" ")
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i > 0 && ["de", "del", "la", "las", "el", "los", "y", "a", "en", "al"].includes(lower)) {
        return lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

/** Títulos que también pertenecen a Superhéroes (además de su categoría principal). */
const SUPERHERO_NEEDLES = [
  "superman",
  "supergirl",
  "batman",
  "spider-man",
  "spiderman",
  "spider man",
  "avengers",
  "vengadores",
  "iron man",
  "hombre de hierro",
  "captain america",
  "capitan america",
  "capitán américa",
  "wonder woman",
  "mujer maravilla",
  "aquaman",
  "flash",
  "x-men",
  "xmen",
  "deadpool",
  "wolverine",
  "thor",
  "black panther",
  "pantera negra",
  "doctor strange",
  "guardianes de la galaxia",
  "guardians of the galaxy",
  "shazam",
  "venom",
  "joker",
  "the batman",
  "justice league",
  "liga de la justicia",
  "caballeros del zodiaco",
];

export function isSuperheroTitle(title) {
  const t = normalizeTitle(title);
  if (!t) return false;
  return SUPERHERO_NEEDLES.some((n) => t.includes(normalizeTitle(n)));
}

/**
 * Lista de categorías de un título (principal + compartidas), ya canónicas.
 * @param {{ category?: string, title?: string, content_type?: string }} video
 * @returns {string[]}
 */
export function videoCategories(video) {
  const primary = canonicalCategory(video?.category);
  const cats = [];
  const push = (name) => {
    const n = canonicalCategory(name);
    if (!n || HIDDEN_CATALOG_CATEGORIES.has(categoryKey(n))) return;
    if (n && !cats.some((c) => categoryKey(c) === categoryKey(n))) cats.push(n);
  };

  // Series anime: solo su categoría (no mezclar Superhéroes de cine).
  if ((video?.content_type || "") === "anime_series") {
    push(primary === "Sin categoría" ? "Anime" : primary);
    return cats;
  }

  // Compat residual: content_type anime (película) → tratar como categoría Anime.
  if ((video?.content_type || "") === "anime") {
    push(
      primary === "Sin categoría" || categoryKey(primary) === "cine" ? "Anime" : primary
    );
    return cats;
  }

  push(primary);
  if (isSuperheroTitle(video?.title)) push("Superhéroes");
  return cats;
}

export function videoInCategory(video, categoryName) {
  const want = categoryKey(categoryName);
  if (!want) return true;
  return videoCategories(video).some((c) => categoryKey(c) === want);
}
